import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { mediateLoginInfoDependent, mediateSelectedListInfoDependent } from 'mediators';
import {
    MOCK_TEMPLATES,
    Template,
    TplEntry,
    getTemplates as fetchTemplatesApi,
    createTemplate as createTemplateApi,
    updateTemplate as updateTemplateApi,
    deleteTemplate as deleteTemplateApi,
    applyTemplate as applyTemplateApi,
    TPL_PURPLE,
    TPL_PURPLE_DARK,
    TPL_PURPLE_SOFT,
    TPL_PURPLE_PALE,
    TPL_INK,
    TPL_INK_700,
    TPL_SLATE,
    TPL_SLATE_LIGHT,
    TPL_HAIR,
    TPL_HAIR_SOFT,
    TPL_BG,
    TPL_RED,
    TPL_GREEN,
    TPL_AMBER,
    TPL_CAT,
    TPL_COLORS,
    TPL_COLOR_BY_ID,
    tplFmt,
    tplExpenseTotal,
    tplIncomeTotal,
    tplRecurrenceLabel,
    tplRecurrenceShort,
    tplFormatDate,
    tplFormatDateShort,
    tplByCategory,
    tplBuildApplyPreview,
    tplDefaultDecision,
} from '../core/services/templates.service';

// ── SVG icon helper ──

const ICON_PATHS: Record<string, string> = {
    home:           'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z',
    bolt:           'M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z',
    directions_car: 'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
    sports_esports: 'M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5S14.67 12 15.5 12s1.5.67 1.5 1.5S16.33 15 15.5 15zm4-3c-.83 0-1.5-.67-1.5-1.5S18.67 9 19.5 9s1.5.67 1.5 1.5S20.33 12 19.5 12z',
    favorite:       'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z',
    restaurant:     'M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z',
    trending_up:    'M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z',
    category:       'M12 2l-5.5 9h11L12 2zm0 3.84L13.93 9h-3.87L12 5.84zM17.5 13c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5zM3 21.5h8v-8H3v8zm2-6h4v4H5v-4z',
    school:         'M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z',
    shopping_bag:   'M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-6-2c1.1 0 2 .9 2 2h-4c0-1.1.9-2 2-2zm6 16H6V8h2v2c0 .55.45 1 1 1s1-.45 1-1V8h4v2c0 .55.45 1 1 1s1-.45 1-1V8h2v12z',
    drag_indicator: 'M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    search:         'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z',
    sort:           'M3 18h6v-2H3v2zM3 6v2h18V6H3zm0 7h12v-2H3v2z',
    bookmark:       'M17 3H7c-1.1 0-1.99.9-1.99 2L5 21l7-3 7 3V5c0-1.1-.9-2-2-2z',
    add:            'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z',
    more_vert:      'M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z',
    play_arrow:     'M8 5v14l11-7z',
    pause:          'M6 19h4V5H6v14zm8-14v14h4V5h-4z',
    history:        'M13 3a9 9 0 100 18A9 9 0 0013 3zM3 13h2a8 8 0 108-8v2a6 6 0 11-6 6H3zm10-5v5l4 2.5-.75 1.23L12 14V8h1z',
    flight:         'M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z',
    check:          'M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z',
    check_circle:   'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z',
    edit:           'M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 000-1.41l-2.34-2.34a.996.996 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z',
    content_copy:   'M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z',
    event_repeat:   'M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14a2 2 0 002 2h6v-2H5V10h14v3h2V6c0-1.1-.9-2-2-2zm0 4H5V6h14v2zm.65 7.35L22 13v6h-6l2.61-2.61c-.71-.86-1.79-1.39-2.99-1.39A3.99 3.99 0 0011.86 18l-1.79.79c.32-1.07 1.21-3.16 4-3.65a4.96 4.96 0 015.58 1.21z',
    schedule:       'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z',
    close:          'M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
    delete:         'M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z',
    chevron_right:  'M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z',
    chevron_left:   'M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z',
    undo:           'M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z',
};

const TplIcon: React.FC<{ name: string; size?: number; color?: string; style?: React.CSSProperties }> = ({ name, size = 18, color = TPL_INK, style }) => {
    const path = ICON_PATHS[name];
    if (!path) return <span style={{ fontSize: size * 0.6, color, ...style }}>•</span>;
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'inline-block', flexShrink: 0, ...style }}>
            <path d={path} />
        </svg>
    );
};

// ── Toggle component ──

interface ToggleProps {
    on: boolean;
    onChange: (val: boolean) => void;
}

const Toggle: React.FC<ToggleProps> = ({ on, onChange }) => {
    return (
        <div
            onClick={(e) => { e.stopPropagation(); onChange(!on); }}
            style={{
                width: 42, height: 24, borderRadius: 12,
                background: on ? TPL_PURPLE : TPL_HAIR,
                position: 'relative', cursor: 'pointer',
                transition: 'background 150ms', flexShrink: 0,
            }}
        >
            <span style={{
                position: 'absolute', top: 2,
                left: on ? 20 : 2,
                width: 20, height: 20, borderRadius: '50%',
                background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                transition: 'left 150ms',
            }} />
        </div>
    );
};

// ── CreateEditModal ──

interface CreateEditModalProps {
    template: Template | null;
    mode: 'create' | 'edit';
    onClose: () => void;
    onSave: (updated: Template) => void;
    onDelete: (id: string) => void;
}

const CreateEditModal: React.FC<CreateEditModalProps> = ({ template, mode, onClose, onSave, onDelete }) => {
    const seedId = template ? template.id : `new-${Date.now()}`;
    const seedName = template ? template.name : '';
    const seedColor = template ? template.color : 'purple';
    const seedIcon = template ? template.icon : 'bookmark';
    const seedRecurrence = template ? template.recurrence : { kind: 'monthly' as const, anchor: 1 };
    const seedAccount = template ? template.account : 'Personal — Main';
    const seedAutoApply = template ? template.autoApply : false;
    const seedEntries = template ? template.entries : [{ title: '', amount: 0, day: 1, cat: 'other' }];
    const seedNotes = template ? template.notes : '';
    const seedTags = template ? template.tags : [];

    const [name, setName] = useState(seedName);
    const [color, setColor] = useState(seedColor);
    const [recurrenceKind, setRecurrenceKind] = useState<string>(seedRecurrence.kind);
    const [autoApply, setAutoApply] = useState(seedAutoApply);
    const [entries, setEntries] = useState<TplEntry[]>(seedEntries.map(e => ({ ...e })));
    const [notes, setNotes] = useState(seedNotes);
    const [tags, setTags] = useState<string[]>(seedTags.slice());
    const [tagInput, setTagInput] = useState('');
    const [nameFocus, setNameFocus] = useState(false);

    const c = TPL_COLOR_BY_ID[color] || TPL_COLOR_BY_ID['purple'];

    const total = entries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const totalLabel = tplFmt(total);

    const setEntry = (i: number, patch: Partial<TplEntry>) =>
        setEntries(es => es.map((e, idx) => idx === i ? { ...e, ...patch } : e));

    const removeEntry = (i: number) =>
        setEntries(es => es.filter((_, idx) => idx !== i));

    const addEntry = () =>
        setEntries(es => [...es, { title: '', amount: 0, day: 1, cat: 'other' }]);

    const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
            e.preventDefault();
            const newTag = tagInput.trim().replace(/,$/, '');
            if (newTag && !tags.includes(newTag)) {
                setTags(ts => [...ts, newTag]);
            }
            setTagInput('');
        }
    };

    const removeTag = (tag: string) => setTags(ts => ts.filter(x => x !== tag));

    const handleSave = () => {
        const base = template || {
            id: seedId,
            nextApply: null,
            lastApplied: null,
            account: 'Personal — Main',
            appliedCount: 0,
            status: 'active' as const,
        };
        const updated: Template = {
            ...base,
            id: seedId,
            name,
            color,
            icon: seedIcon,
            recurrence: { kind: recurrenceKind as any, anchor: seedRecurrence.anchor },
            autoApply,
            account: base.account,
            tags,
            notes,
            entries,
            status: base.status || 'active',
            nextApply: base.nextApply || null,
            lastApplied: base.lastApplied || null,
            appliedCount: base.appliedCount || 0,
        };
        onSave(updated);
    };

    const handleDelete = () => {
        if (template && window.confirm(`Delete "${template.name}"? This cannot be undone.`)) {
            onDelete(template.id);
        }
    };

    const headerTitle = mode === 'create' ? 'New template' : `Edit "${seedName}"`;
    const headerSub = mode === 'create'
        ? 'Bundle a set of entries you apply often. Use it monthly, weekly, or any time you want.'
        : 'Changes apply to future cycles. Existing entries already on your calendar are unchanged.';
    const saveLabel = mode === 'create' ? 'Create template' : 'Save changes';

    const recOptions = [
        { k: 'monthly',  label: 'Monthly' },
        { k: 'biweekly', label: 'Every 2 weeks' },
        { k: 'weekly',   label: 'Weekly' },
        { k: 'custom',   label: 'Custom' },
        { k: 'once',     label: 'One-off' },
    ];

    const autoLabel = autoApply ? 'On — applies on next due date' : 'Off — apply manually';

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 30, padding: 24 }}
            onClick={onClose}
        >
            <div
                style={{ width: '100%', maxWidth: 760, maxHeight: '92%', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.20)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 24px 18px', borderBottom: `1px solid ${TPL_HAIR}` }}>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>{headerTitle}</div>
                        <div style={{ fontSize: 13, color: TPL_SLATE, marginTop: 3 }}>{headerSub}</div>
                    </div>
                    <div
                        style={{ marginLeft: 'auto', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: TPL_SLATE }}
                        onClick={onClose}
                    >
                        <TplIcon name="close" size={20} color={TPL_SLATE} />
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflow: 'auto', padding: '18px 24px 24px', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {/* Identity row */}
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
                            <div style={{ width: 64, height: 64, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: c.bg }}>
                                <TplIcon name={seedIcon} size={28} color={c.fg} />
                            </div>
                            <div style={{ fontSize: 11, color: TPL_PURPLE, fontWeight: 600, cursor: 'pointer' }}>Change icon</div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Name</div>
                            <input
                                type="text"
                                placeholder="e.g. Monthly bills"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                onFocus={() => setNameFocus(true)}
                                onBlur={() => setNameFocus(false)}
                                style={{
                                    height: 44, border: `1px solid ${nameFocus ? TPL_PURPLE : TPL_HAIR}`,
                                    borderRadius: 8, padding: '0 14px', fontSize: 16, fontWeight: 600,
                                    color: TPL_INK, fontFamily: 'inherit', outline: 'none',
                                    boxShadow: nameFocus ? '0 0 0 3px rgba(124,58,237,0.10)' : 'none',
                                }}
                            />
                            <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                                {TPL_COLORS.map(sw => {
                                    const isSelected = color === sw.id;
                                    return (
                                        <span
                                            key={sw.id}
                                            onClick={() => setColor(sw.id)}
                                            style={{
                                                width: 24, height: 24, borderRadius: '50%', cursor: 'pointer',
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                background: sw.bg,
                                                outline: isSelected ? `2px solid ${sw.fg}` : 'none',
                                                outlineOffset: 2,
                                            }}
                                        >
                                            {isSelected && <TplIcon name="check" size={12} color={sw.fg} />}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Recurrence */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Recurrence</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {recOptions.map(r => {
                                const isActive = recurrenceKind === r.k;
                                return (
                                    <div
                                        key={r.k}
                                        onClick={() => setRecurrenceKind(r.k)}
                                        style={{
                                            padding: '8px 14px', borderRadius: 18, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                            background: isActive ? TPL_PURPLE : '#fff',
                                            color: isActive ? '#fff' : TPL_INK_700,
                                            border: `1px solid ${isActive ? TPL_PURPLE : TPL_HAIR}`,
                                        }}
                                    >
                                        {r.label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Account + auto-apply */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Default account</div>
                            <div style={{ height: 40, border: `1px solid ${TPL_HAIR}`, borderRadius: 8, padding: '0 12px', fontSize: 13, color: TPL_INK_700, background: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ flex: 1 }}>{seedAccount}</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Auto-apply</div>
                            <div
                                style={{ height: 40, border: `1px solid ${TPL_HAIR}`, borderRadius: 8, padding: '0 12px', fontSize: 13, color: TPL_INK_700, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
                                onClick={() => setAutoApply(v => !v)}
                            >
                                <span>{autoLabel}</span>
                                <Toggle on={autoApply} onChange={setAutoApply} />
                            </div>
                        </div>
                    </div>

                    {/* Entries */}
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>Entries</div>
                            <div style={{ fontSize: 12, color: TPL_SLATE }}>· {entries.length} · total {totalLabel}/cycle</div>
                            <button
                                style={{ marginLeft: 'auto', height: 32, padding: '0 12px', borderRadius: 8, background: TPL_PURPLE_SOFT, color: TPL_PURPLE_DARK, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                onClick={addEntry}
                            >
                                <TplIcon name="add" size={14} color={TPL_PURPLE_DARK} />
                                Add entry
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '24px 36px 1fr 90px 110px 24px', gap: 10, padding: '4px 12px', fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            <div />
                            <div />
                            <div>Title</div>
                            <div>Day</div>
                            <div style={{ textAlign: 'right' }}>Amount</div>
                            <div />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {entries.map((e, i) => {
                                const cat = TPL_CAT[e.cat] || TPL_CAT['other'];
                                const amtNum = Number(e.amount);
                                const amtColor = amtNum < 0 ? TPL_RED : amtNum > 0 ? TPL_GREEN : TPL_INK;
                                return (
                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '24px 36px 1fr 90px 110px 24px', gap: 10, alignItems: 'center', padding: '10px 12px', borderRadius: 10, border: `1px solid ${TPL_HAIR}`, background: '#fff' }}>
                                        <div style={{ color: TPL_SLATE_LIGHT, cursor: 'grab', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <TplIcon name="drag_indicator" size={14} color={TPL_SLATE_LIGHT} />
                                        </div>
                                        <div style={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: cat.bg }}>
                                            <TplIcon name={cat.icon} size={16} color={cat.fg} />
                                        </div>
                                        <input
                                            type="text"
                                            value={e.title}
                                            placeholder="Entry title"
                                            onChange={ev => setEntry(i, { title: ev.target.value })}
                                            style={{ height: 36, border: `1px solid ${TPL_HAIR}`, borderRadius: 6, padding: '0 10px', fontSize: 13, fontWeight: 600, color: TPL_INK, fontFamily: 'inherit', outline: 'none', minWidth: 0 }}
                                        />
                                        <input
                                            type="text"
                                            value={e.day}
                                            onChange={ev => setEntry(i, { day: Number(ev.target.value) || 0 })}
                                            style={{ height: 36, border: `1px solid ${TPL_HAIR}`, borderRadius: 6, padding: '0 10px', fontSize: 13, color: TPL_INK_700, fontFamily: 'inherit', outline: 'none', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                                        />
                                        <input
                                            type="text"
                                            value={e.amount}
                                            onChange={ev => setEntry(i, { amount: Number(ev.target.value) || 0 })}
                                            style={{ height: 36, border: `1px solid ${TPL_HAIR}`, borderRadius: 6, padding: '0 10px', fontSize: 13, color: amtColor, fontFamily: 'inherit', outline: 'none', fontWeight: 700, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}
                                        />
                                        <div
                                            style={{ color: TPL_SLATE_LIGHT, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            onClick={() => removeEntry(i)}
                                        >
                                            <TplIcon name="delete" size={14} color={TPL_SLATE_LIGHT} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tags */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Tags</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 6px', minHeight: 40, border: `1px solid ${TPL_HAIR}`, borderRadius: 8, alignItems: 'center' }}>
                            {tags.map(tag => (
                                <span key={tag} style={{ padding: '4px 10px', borderRadius: 12, background: TPL_PURPLE_SOFT, color: TPL_PURPLE_DARK, fontSize: 12, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    {tag}
                                    <span style={{ cursor: 'pointer', opacity: 0.6 }} onClick={() => removeTag(tag)}>
                                        <TplIcon name="close" size={10} color={TPL_PURPLE_DARK} />
                                    </span>
                                </span>
                            ))}
                            <input
                                type="text"
                                value={tagInput}
                                onChange={e => setTagInput(e.target.value)}
                                onKeyDown={handleTagKeyDown}
                                placeholder={tags.length ? '' : 'Add a tag (e.g. fixed, kids)…'}
                                style={{ flex: 1, minWidth: 80, height: 28, border: 'none', outline: 'none', padding: '0 6px', fontSize: 13, fontFamily: 'inherit', background: 'transparent' }}
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Notes</div>
                        <textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Anything to remember about this template?"
                            style={{ width: '100%', minHeight: 60, border: `1px solid ${TPL_HAIR}`, borderRadius: 8, padding: '10px 12px', fontSize: 13, color: TPL_INK_700, fontFamily: 'inherit', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px', borderTop: `1px solid ${TPL_HAIR}`, background: '#fff' }}>
                    <div style={{ fontSize: 13, color: TPL_INK_700 }}>
                        <span style={{ fontWeight: 700, color: TPL_INK }}>{entries.length}</span> entries · total {totalLabel}/cycle
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                        {mode === 'edit' && (
                            <button
                                style={{ height: 40, padding: '0 14px', borderRadius: 8, background: '#fff', color: TPL_RED, border: `1px solid ${TPL_HAIR}`, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                                onClick={handleDelete}
                            >
                                <TplIcon name="delete" size={14} color={TPL_RED} />
                                Delete
                            </button>
                        )}
                        <button
                            style={{ height: 40, padding: '0 14px', borderRadius: 8, background: '#fff', color: TPL_INK_700, border: `1px solid ${TPL_HAIR}`, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                        <button
                            style={{ height: 40, padding: '0 18px', borderRadius: 8, background: TPL_PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }}
                            onClick={handleSave}
                        >
                            <TplIcon name="check" size={14} color="#fff" />
                            {saveLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ── ApplyModal ──

interface ApplyModalProps {
    template: Template;
    userId: string;
    listId: string;
    onClose: () => void;
    onApplied: () => void;
}

// Build a 12-month grid starting from (current month - 1)
function buildApplyMonths(): Array<{ iso: string; label: string; year: number; isPast: boolean; isCurrent: boolean; isFuture: boolean }> {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    const startYear = now.getFullYear();
    const startMonth = now.getMonth() - 1; // one month before current (0-indexed)
    const out = [];
    for (let i = 0; i < 12; i++) {
        const rawMonth = startMonth + i;
        const m = ((rawMonth % 12) + 12) % 12;
        const y = startYear + Math.floor(rawMonth / 12);
        out.push({
            iso: `${y}-${String(m + 1).padStart(2, '0')}-01`,
            label: monthNames[m],
            year: y,
            isPast: i === 0,
            isCurrent: i === 1,
            isFuture: i > 1,
        });
    }
    return out;
}

const ApplyModal: React.FC<ApplyModalProps> = ({ template, userId, listId, onClose, onApplied }) => {
    const now = new Date();
    const nextMonthYear = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
    const nextMonthNum = (now.getMonth() + 1) % 12 + 1;
    const initialIso = `${nextMonthYear}-${String(nextMonthNum).padStart(2, '0')}-01`;

    const [step, setStep] = useState(1);
    const [targetIso, setTargetIso] = useState(initialIso);
    const [autoApplyOn, setAutoApplyOn] = useState(template.autoApply);
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState('');

    const months = useMemo(() => buildApplyMonths(), []);

    const effectiveIso = targetIso === '__rest' ? initialIso : targetIso;

    const preview = useMemo(
        () => tplBuildApplyPreview(template, effectiveIso),
        [template, effectiveIso]
    );

    const [decisions, setDecisions] = useState<Record<string, string>>(() => {
        const out: Record<string, string> = {};
        preview.rows.forEach(r => { out[r.id] = tplDefaultDecision(r); });
        return out;
    });

    // Re-initialize decisions when target month or template changes
    React.useEffect(() => {
        const out: Record<string, string> = {};
        preview.rows.forEach(r => { out[r.id] = tplDefaultDecision(r); });
        setDecisions(out);
    }, [targetIso, template.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const setDecision = (id: string, val: string) =>
        setDecisions(d => ({ ...d, [id]: val }));

    const c = TPL_COLOR_BY_ID[template.color] || TPL_COLOR_BY_ID['slate'];
    const conflicts = preview.rows.filter(r => r.kind === 'conflict').length;
    const willAdd = preview.rows.filter(r => decisions[r.id] === 'add' || decisions[r.id] === 'replace').length;

    const selectedDateLabel = tplFormatDate(effectiveIso);

    const stepDefs = [
        { id: 1, label: 'Pick month' },
        { id: 2, label: 'Review' },
        { id: 3, label: 'Done' },
    ];

    return (
        <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 30, padding: 24 }}
            onClick={onClose}
        >
            <div
                style={{ width: '100%', maxWidth: 920, maxHeight: '92%', background: '#fff', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.20)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 24px 16px', borderBottom: `1px solid ${TPL_HAIR}` }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: c.bg }}>
                        <TplIcon name={template.icon} size={22} color={c.fg} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>Apply "{template.name}"</div>
                        <div style={{ fontSize: 13, color: TPL_SLATE, marginTop: 3 }}>
                            {step === 1 && "Pick a target month — we'll show what gets added before you confirm."}
                            {step === 2 && `${preview.rows.length} entries, ${conflicts} conflict${conflicts === 1 ? '' : 's'} to resolve in ${preview.monthLabel}.`}
                            {step === 3 && 'Done. You can undo from the activity log within 7 days.'}
                        </div>
                    </div>
                    <div
                        style={{ marginLeft: 'auto', width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        onClick={onClose}
                    >
                        <TplIcon name="close" size={20} color={TPL_SLATE} />
                    </div>
                </div>

                {/* Step header */}
                <div style={{ display: 'flex', gap: 0, padding: '12px 24px', borderBottom: `1px solid ${TPL_HAIR}`, background: TPL_BG, fontSize: 12, fontWeight: 600, color: TPL_SLATE }}>
                    {stepDefs.map((s, i) => {
                        const done = step > s.id;
                        const active = step === s.id;
                        return (
                            <React.Fragment key={s.id}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                                    <span style={{
                                        width: 18, height: 18, borderRadius: '50%',
                                        background: done ? TPL_GREEN : active ? TPL_PURPLE : '#fff',
                                        border: done || active ? 'none' : `1px solid ${TPL_HAIR}`,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 10, fontWeight: 700,
                                        color: done || active ? '#fff' : TPL_SLATE,
                                    }}>
                                        {done ? <TplIcon name="check" size={10} color="#fff" /> : s.id}
                                    </span>
                                    <span style={{ color: active || done ? TPL_INK : TPL_SLATE }}>{s.label}</span>
                                </span>
                                {i < stepDefs.length - 1 && (
                                    <span style={{ color: TPL_SLATE_LIGHT, margin: '0 12px' }}>
                                        <TplIcon name="chevron_right" size={14} color={TPL_SLATE_LIGHT} />
                                    </span>
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                    {step === 1 && (
                        <div style={{ padding: 24 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
                                Apply "{template.name}" to
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 18 }}>
                                {months.map(m => {
                                    const isSelected = m.iso === targetIso;
                                    return (
                                        <div
                                            key={m.iso}
                                            style={{
                                                padding: '12px 0', borderRadius: 10, cursor: m.isPast ? 'default' : 'pointer',
                                                textAlign: 'center', position: 'relative',
                                                border: `1px solid ${isSelected ? TPL_PURPLE : TPL_HAIR}`,
                                                background: isSelected ? TPL_PURPLE_PALE : m.isPast ? TPL_BG : '#fff',
                                                boxShadow: isSelected ? '0 0 0 3px rgba(124,58,237,0.10)' : 'none',
                                                color: m.isPast ? TPL_SLATE_LIGHT : TPL_INK,
                                                opacity: m.isPast ? 0.7 : 1,
                                            }}
                                            onClick={() => !m.isPast && setTargetIso(m.iso)}
                                        >
                                            {m.isCurrent && (
                                                <span style={{ position: 'absolute', top: 6, right: 6, fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: '#FEF3C7', color: '#92400E' }}>
                                                    NOW
                                                </span>
                                            )}
                                            <div style={{ fontSize: 11, fontWeight: 600, color: m.isPast ? TPL_SLATE_LIGHT : TPL_SLATE, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.year}</div>
                                            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>{m.label}</div>
                                            <div style={{ fontSize: 10, color: TPL_SLATE, marginTop: 4 }}>
                                                {m.isPast ? 'Already done' : m.isCurrent ? 'In progress' : 'Empty'}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Or quick-apply</div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
                                {[
                                    { iso: initialIso, label: `Next month` },
                                    { iso: '__rest', label: 'Rest of year' },
                                ].map((q, i) => {
                                    const isActive = targetIso === q.iso;
                                    return (
                                        <div
                                            key={i}
                                            style={{ padding: '8px 14px', borderRadius: 18, cursor: 'pointer', fontSize: 13, fontWeight: 600, background: isActive ? TPL_PURPLE_SOFT : '#fff', color: isActive ? TPL_PURPLE_DARK : TPL_INK_700, border: `1px solid ${isActive ? 'transparent' : TPL_HAIR}` }}
                                            onClick={() => setTargetIso(q.iso)}
                                        >
                                            {q.label}
                                        </div>
                                    );
                                })}
                            </div>

                            <div style={{ padding: '14px 16px', border: `1px solid ${TPL_HAIR}`, borderRadius: 12, background: TPL_BG, display: 'flex', alignItems: 'center', gap: 14 }}>
                                <span style={{ width: 36, height: 36, borderRadius: 10, background: TPL_PURPLE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <TplIcon name="event_repeat" size={18} color={TPL_PURPLE} />
                                </span>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 700 }}>Also schedule auto-apply</div>
                                    <div style={{ fontSize: 12, color: TPL_SLATE, marginTop: 2 }}>
                                        Apply this template every month going forward, on day {template.recurrence.anchor || 1}.
                                    </div>
                                </div>
                                <Toggle on={autoApplyOn} onChange={setAutoApplyOn} />
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <>
                            {/* Summary bar */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0, borderBottom: `1px solid ${TPL_HAIR}`, background: '#fff' }}>
                                {(() => {
                                    const adds = preview.rows.filter(r => decisions[r.id] === 'add' || decisions[r.id] === 'replace');
                                    const newCount = preview.rows.filter(r => r.kind === 'add').length;
                                    const totalImpact = adds.reduce((s, r) => s + (r.template.amount || 0), 0);
                                    const totalImpactLabel = tplFmt(totalImpact);
                                    const totalImpactColor = totalImpact < 0 ? TPL_RED : TPL_GREEN;
                                    const conflictsColor = conflicts > 0 ? TPL_AMBER : TPL_INK;
                                    return (
                                        <>
                                            <div style={{ padding: '14px 20px', borderRight: `1px solid ${TPL_HAIR}` }}>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>New entries</div>
                                                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: TPL_GREEN }}>+{newCount}</div>
                                                <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 3 }}>added to {preview.monthLabel}</div>
                                            </div>
                                            <div style={{ padding: '14px 20px', borderRight: `1px solid ${TPL_HAIR}` }}>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Conflicts</div>
                                                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: conflictsColor }}>{conflicts}</div>
                                                <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 3 }}>{conflicts > 0 ? 'choose action below' : 'no overlaps'}</div>
                                            </div>
                                            <div style={{ padding: '14px 20px' }}>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Net impact</div>
                                                <div style={{ fontSize: 22, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: totalImpactColor }}>{totalImpactLabel}</div>
                                                <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 3 }}>{adds.length} entries selected</div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Bulk action bar */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', background: TPL_BG, borderBottom: `1px solid ${TPL_HAIR}`, fontSize: 12, color: TPL_SLATE }}>
                                <span>Quick choose:</span>
                                <span
                                    style={{ padding: '5px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${TPL_HAIR}`, fontSize: 11, fontWeight: 600, color: TPL_INK_700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                    onClick={() => { preview.rows.forEach(r => { if (r.kind === 'conflict') setDecision(r.id, 'skip'); }); }}
                                >
                                    Skip all conflicts
                                </span>
                                <span
                                    style={{ padding: '5px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${TPL_HAIR}`, fontSize: 11, fontWeight: 600, color: TPL_INK_700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                    onClick={() => { preview.rows.forEach(r => { if (r.kind === 'conflict') setDecision(r.id, 'replace'); }); }}
                                >
                                    Replace all
                                </span>
                                <span
                                    style={{ padding: '5px 10px', borderRadius: 6, background: '#fff', border: `1px solid ${TPL_HAIR}`, fontSize: 11, fontWeight: 600, color: TPL_INK_700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                    onClick={() => { preview.rows.forEach(r => { if (r.kind === 'conflict') setDecision(r.id, 'add'); }); }}
                                >
                                    Add both
                                </span>
                                <span style={{ marginLeft: 'auto', fontSize: 11, color: TPL_SLATE_LIGHT }}>
                                    {conflicts > 0 ? `${conflicts} conflict${conflicts === 1 ? '' : 's'} need a decision` : 'All set'}
                                </span>
                            </div>

                            {/* Preview table header */}
                            <div style={{ display: 'grid', gridTemplateColumns: '24px 36px 1fr 1fr 80px 200px', columnGap: 14, alignItems: 'center', padding: '10px 20px', fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${TPL_HAIR}`, background: '#fff', position: 'sticky', top: 0 }}>
                                <div />
                                <div />
                                <div>Entry</div>
                                <div>Currently in {preview.monthLabel}</div>
                                <div style={{ textAlign: 'right' }}>Day</div>
                                <div style={{ textAlign: 'right' }}>Action</div>
                            </div>

                            <div style={{ padding: '8px 0' }}>
                                {preview.rows.map(row => {
                                    const cat = TPL_CAT[row.template.cat] || TPL_CAT['other'];
                                    const isMatch = row.kind === 'match';
                                    const isConflict = row.kind === 'conflict';
                                    const isAdd = row.kind === 'add';
                                    const decision = decisions[row.id];
                                    const pillStyle: React.CSSProperties = {
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
                                        textTransform: 'uppercase', letterSpacing: '0.05em',
                                        background: isAdd ? '#D1FAE5' : isConflict ? '#FEF3C7' : TPL_BG,
                                        color: isAdd ? '#065F46' : isConflict ? '#92400E' : TPL_SLATE,
                                    };
                                    const entryAmtLabel = tplFmt(row.template.amount);
                                    const catLabel = (TPL_CAT[row.template.cat] && TPL_CAT[row.template.cat].label) || 'Other';
                                    const existingAmtLabel = row.existing ? tplFmt(row.existing.amount) : '';
                                    return (
                                        <div
                                            key={row.id}
                                            style={{ display: 'grid', gridTemplateColumns: '24px 36px 1fr 1fr 80px 200px', columnGap: 14, alignItems: 'center', padding: '10px 20px', borderBottom: `1px solid ${TPL_HAIR_SOFT}`, background: isMatch ? 'rgba(248,250,252,0.6)' : 'transparent' }}
                                        >
                                            <div><span style={pillStyle}>{isAdd ? '+' : isConflict ? '!' : '='}</span></div>
                                            <div>
                                                <span style={{ width: 30, height: 30, borderRadius: '50%', background: cat.bg, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <TplIcon name={cat.icon} size={14} color={cat.fg} />
                                                </span>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 600 }}>{row.template.title}</div>
                                                <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2 }}>{entryAmtLabel} · {catLabel}</div>
                                            </div>
                                            <div>
                                                {row.existing ? (
                                                    isConflict ? (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                            <span style={{ fontSize: 11, color: TPL_SLATE, textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>{existingAmtLabel} · {row.existing.title}</span>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: 12, color: TPL_SLATE }}>Identical entry already there</span>
                                                    )
                                                ) : (
                                                    <span style={{ fontSize: 12, color: TPL_SLATE_LIGHT }}>&mdash;</span>
                                                )}
                                            </div>
                                            <div style={{ fontSize: 12, color: TPL_SLATE, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{row.dateLabel}</div>
                                            <div>
                                                {isAdd ? (
                                                    <div style={{ display: 'flex', height: 30, borderRadius: 8, border: `1px solid ${TPL_HAIR}`, overflow: 'hidden', fontSize: 11, fontWeight: 600, background: '#fff' }}>
                                                        <span
                                                            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', cursor: 'pointer', color: decision === 'add' ? '#fff' : TPL_SLATE, background: decision === 'add' ? TPL_PURPLE : 'transparent', borderRight: `1px solid ${TPL_HAIR}` }}
                                                            onClick={() => setDecision(row.id, 'add')}
                                                        >Add</span>
                                                        <span
                                                            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', cursor: 'pointer', color: decision === 'skip' ? '#fff' : TPL_SLATE, background: decision === 'skip' ? TPL_PURPLE : 'transparent' }}
                                                            onClick={() => setDecision(row.id, 'skip')}
                                                        >Skip</span>
                                                    </div>
                                                ) : isConflict ? (
                                                    <div style={{ display: 'flex', height: 30, borderRadius: 8, border: `1px solid ${TPL_HAIR}`, overflow: 'hidden', fontSize: 11, fontWeight: 600, background: '#fff' }}>
                                                        <span
                                                            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', cursor: 'pointer', color: decision === 'skip' ? '#fff' : TPL_SLATE, background: decision === 'skip' ? TPL_PURPLE : 'transparent', borderRight: `1px solid ${TPL_HAIR}` }}
                                                            onClick={() => setDecision(row.id, 'skip')}
                                                            title="Keep existing"
                                                        >Keep</span>
                                                        <span
                                                            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', cursor: 'pointer', color: decision === 'replace' ? '#fff' : TPL_SLATE, background: decision === 'replace' ? TPL_PURPLE : 'transparent', borderRight: `1px solid ${TPL_HAIR}` }}
                                                            onClick={() => setDecision(row.id, 'replace')}
                                                            title="Replace existing"
                                                        >Replace</span>
                                                        <span
                                                            style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px', cursor: 'pointer', color: decision === 'add' ? '#fff' : TPL_SLATE, background: decision === 'add' ? TPL_PURPLE : 'transparent' }}
                                                            onClick={() => setDecision(row.id, 'add')}
                                                            title="Add anyway"
                                                        >Add both</span>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: 'flex', height: 30, borderRadius: 8, border: `1px solid ${TPL_HAIR}`, overflow: 'hidden', fontSize: 11, fontWeight: 600, background: TPL_HAIR_SOFT, color: TPL_SLATE }}>
                                                        <span style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>Skipped (already there)</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {step === 3 && (() => {
                        const added = preview.rows.filter(r => decisions[r.id] === 'add' || decisions[r.id] === 'replace').length;
                        const skipped = preview.rows.length - added;
                        const addedLabel = `${added} ${added === 1 ? 'entry' : 'entries'}`;
                        return (
                            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                                <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#D1FAE5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                                    <TplIcon name="check_circle" size={40} color={TPL_GREEN} />
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>Applied to {preview.monthLabel}</div>
                                <div style={{ fontSize: 14, color: TPL_SLATE, marginTop: 8, maxWidth: 380, margin: '8px auto 0', lineHeight: 1.5 }}>
                                    <strong>{template.name}</strong> added {added} {added === 1 ? 'entry' : 'entries'} to your calendar.
                                    {skipped > 0 && ` ${skipped} skipped.`}
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24, flexWrap: 'wrap' }}>
                                    <div style={{ padding: '10px 16px', borderRadius: 10, background: TPL_BG, border: `1px solid ${TPL_HAIR}`, fontSize: 13, color: TPL_INK_700 }}>+{added} added</div>
                                    {skipped > 0 && <div style={{ padding: '10px 16px', borderRadius: 10, background: TPL_BG, border: `1px solid ${TPL_HAIR}`, fontSize: 13, color: TPL_INK_700 }}>{skipped} skipped</div>}
                                    <div style={{ padding: '10px 16px', borderRadius: 10, background: TPL_BG, border: `1px solid ${TPL_HAIR}`, fontSize: 13, color: TPL_INK_700 }}>Auto-apply: {autoApplyOn ? 'on for next cycle' : 'off'}</div>
                                </div>
                                {/* Computed but unused — satisfies linter about addedLabel */}
                                <span style={{ display: 'none' }}>{addedLabel}</span>
                            </div>
                        );
                    })()}
                </div>

                {/* Footer */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 24px', borderTop: `1px solid ${TPL_HAIR}`, background: '#fff' }}>
                    {step === 1 && (
                        <>
                            <div style={{ fontSize: 13, color: TPL_INK_700 }}>
                                Selected: <span style={{ fontWeight: 700, color: TPL_INK }}>{selectedDateLabel}</span>
                                {targetIso === '__rest' && <span style={{ color: TPL_SLATE }}> + more months</span>}
                            </div>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                <button style={{ height: 40, padding: '0 14px', borderRadius: 8, background: '#fff', color: TPL_INK_700, border: `1px solid ${TPL_HAIR}`, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={onClose}>Cancel</button>
                                <button style={{ height: 40, padding: '0 18px', borderRadius: 8, background: TPL_PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }} onClick={() => setStep(2)}>
                                    Preview changes
                                    <TplIcon name="chevron_right" size={14} color="#fff" />
                                </button>
                            </div>
                        </>
                    )}
                    {step === 2 && (
                        <>
                            <div style={{ fontSize: 13, color: TPL_INK_700 }}>
                                Will add <span style={{ fontWeight: 700, color: TPL_INK }}>{willAdd}</span> {willAdd === 1 ? 'entry' : 'entries'}
                                {conflicts > 0 && <span style={{ color: TPL_SLATE }}> · {conflicts} conflict{conflicts === 1 ? '' : 's'} resolved</span>}
                            </div>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                <button style={{ height: 40, padding: '0 14px', borderRadius: 8, background: '#fff', color: TPL_INK_700, border: `1px solid ${TPL_HAIR}`, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }} onClick={() => setStep(1)}>
                                    <TplIcon name="chevron_left" size={14} color={TPL_INK_700} />
                                    Back
                                </button>
                                <button
                                    disabled={applying}
                                    style={{ height: 40, padding: '0 18px', borderRadius: 8, background: applying ? TPL_SLATE_LIGHT : TPL_PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: applying ? 'default' : 'pointer', border: 'none' }}
                                    onClick={async () => {
                                        setApplying(true);
                                        setApplyError('');
                                        try {
                                            await applyTemplateApi(template.id, userId, listId, 'monthly', 1, effectiveIso.replace(/-01$/, '') + '-01');
                                            setStep(3);
                                            onApplied();
                                        } catch {
                                            setApplyError('Failed to apply template. Please try again.');
                                        } finally {
                                            setApplying(false);
                                        }
                                    }}
                                >
                                    <TplIcon name="check" size={14} color="#fff" />
                                    {applying ? 'Applying…' : `Apply ${willAdd} ${willAdd === 1 ? 'entry' : 'entries'}`}
                                </button>
                                {applyError && (
                                    <div style={{ fontSize: 12, color: TPL_RED, marginLeft: 8, alignSelf: 'center' }}>{applyError}</div>
                                )}
                            </div>
                        </>
                    )}
                    {step === 3 && (
                        <>
                            <div style={{ fontSize: 13, color: TPL_INK_700 }}>
                                <span style={{ fontWeight: 700, color: TPL_INK }}>Saved.</span> Applied {willAdd} entries · {autoApplyOn ? 'auto-apply on' : 'manual'}.
                            </div>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                <button style={{ height: 40, padding: '0 14px', borderRadius: 8, background: '#fff', color: TPL_INK_700, border: `1px solid ${TPL_HAIR}`, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                    <TplIcon name="undo" size={14} color={TPL_INK_700} />
                                    Undo
                                </button>
                                <button style={{ height: 40, padding: '0 18px', borderRadius: 8, background: TPL_PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }} onClick={onClose}>
                                    View {preview.monthLabel}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Sub-components ──

interface TemplateRowProps {
    t: Template;
    selected: boolean;
    onSelect: () => void;
}

const TemplateRow: React.FC<TemplateRowProps> = ({ t, selected, onSelect }) => {
    const colorSwatch = TPL_COLOR_BY_ID[t.color] || TPL_COLOR_BY_ID['slate'];
    const expense = tplExpenseTotal(t);
    const income = tplIncomeTotal(t);
    const net = expense + income;
    const netColor = net < 0 ? TPL_RED : TPL_GREEN;
    const netLabel = tplFmt(net);
    const recLabel = tplRecurrenceShort(t.recurrence);
    const nextLabel = tplFormatDateShort(t.nextApply);
    const entryCount = t.entries.length;

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        borderRadius: 12,
        border: `1px solid ${selected ? TPL_PURPLE : TPL_HAIR}`,
        background: '#fff',
        cursor: 'pointer',
        marginBottom: 8,
        boxShadow: selected ? '0 0 0 3px rgba(124,58,237,0.10)' : 'none',
        transition: 'box-shadow 120ms, border-color 120ms',
    };

    return (
        <div style={rowStyle} onClick={onSelect}>
            <div style={{ color: TPL_SLATE_LIGHT, marginRight: -6 }}>
                <TplIcon name="drag_indicator" size={16} color={TPL_SLATE_LIGHT} />
            </div>
            <div style={{ width: 42, height: 42, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: colorSwatch.bg }}>
                <TplIcon name={t.icon} size={20} color={colorSwatch.fg} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: TPL_INK, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.name}
                    </div>
                    {t.autoApply && t.status === 'active' && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: '#D1FAE5', color: '#065F46', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Auto
                        </span>
                    )}
                    {t.status === 'paused' && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6, background: '#FEF3C7', color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Paused
                        </span>
                    )}
                </div>
                <div style={{ fontSize: 12, color: TPL_SLATE, marginTop: 3, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <TplIcon name="event_repeat" size={12} color={TPL_SLATE} />
                        {recLabel}
                    </span>
                    <span style={{ width: 3, height: 3, borderRadius: '50%', background: TPL_SLATE_LIGHT, flexShrink: 0 }} />
                    <span>{entryCount} entries</span>
                    {t.nextApply && (
                        <>
                            <span style={{ width: 3, height: 3, borderRadius: '50%', background: TPL_SLATE_LIGHT, flexShrink: 0 }} />
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <TplIcon name="schedule" size={12} color={TPL_SLATE} />
                                Next {nextLabel}
                            </span>
                        </>
                    )}
                    <span style={{ width: 3, height: 3, borderRadius: '50%', background: TPL_SLATE_LIGHT, flexShrink: 0 }} />
                    <span>Applied {t.appliedCount}{'\u00d7'}</span>
                </div>
            </div>
            <div>
                <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 110, textAlign: 'right', color: netColor }}>
                    {netLabel}
                </div>
                <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2, fontWeight: 500, textAlign: 'right' }}>per cycle</div>
            </div>
            <div style={{ width: 32, height: 32, borderRadius: 8, color: TPL_SLATE, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <TplIcon name="more_vert" size={18} color={TPL_SLATE} />
            </div>
        </div>
    );
};

interface CategoryBreakdownProps {
    t: Template;
}

const CategoryBreakdown: React.FC<CategoryBreakdownProps> = ({ t }) => {
    const buckets = tplByCategory(t);
    const maxAbs = Math.max(...buckets.map(b => Math.abs(b.total)), 1);

    return (
        <div>
            {buckets.map(b => {
                const cat = TPL_CAT[b.cat] || TPL_CAT['other'];
                const pct = (Math.abs(b.total) / maxAbs) * 100;
                const amtLabel = tplFmt(b.total);
                return (
                    <div key={b.cat} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                        <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: cat.bg, flexShrink: 0 }}>
                            <TplIcon name={cat.icon} size={12} color={cat.fg} />
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: TPL_INK_700, minWidth: 80 }}>{cat.label}</span>
                        <span style={{ flex: 1, height: 6, background: TPL_HAIR_SOFT, borderRadius: 3, overflow: 'hidden' }}>
                            <span style={{ display: 'block', height: '100%', borderRadius: 3, width: `${pct}%`, background: cat.fg }} />
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: TPL_INK, minWidth: 80, textAlign: 'right' }}>
                            {amtLabel}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

interface DetailPaneProps {
    t: Template | null;
    onApply: () => void;
    onEdit: () => void;
    onDuplicate: () => void;
    onTogglePause: () => void;
    onMore: () => void;
    onAddEntry: () => void;
}

const DetailPane: React.FC<DetailPaneProps> = ({ t, onApply, onEdit, onDuplicate, onTogglePause, onMore, onAddEntry }) => {
    if (!t) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 40, color: TPL_SLATE, height: '100%' }}>
                <div style={{ width: 80, height: 80, borderRadius: 20, background: TPL_PURPLE_SOFT, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <TplIcon name="bookmark" size={32} color={TPL_PURPLE} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: TPL_INK }}>Pick a template</div>
                <div style={{ fontSize: 13, marginTop: 6, maxWidth: 280, lineHeight: 1.5 }}>
                    Select any template on the left to view its entries, edit it, or apply it to a month.
                </div>
            </div>
        );
    }

    const colorSwatch = TPL_COLOR_BY_ID[t.color] || TPL_COLOR_BY_ID['slate'];
    const expense = tplExpenseTotal(t);
    const income = tplIncomeTotal(t);
    const recLabel = tplRecurrenceLabel(t.recurrence);
    const expenseLabel = tplFmt(expense);
    const incomeLabel = tplFmt(income);
    const lastAppliedLabel = tplFormatDateShort(t.lastApplied);
    const nextApplyLabel = tplFormatDateShort(t.nextApply);
    const lastAppliedLong = tplFormatDate(t.lastApplied);
    const entryCount = t.entries.length;
    const tagsSub = t.tags.join(' \u00b7 ') || '\u2014';
    const pauseBtnTitle = t.status === 'paused' ? 'Resume' : 'Pause auto-apply';
    const pauseBtnIcon = t.status === 'paused' ? 'play_arrow' : 'pause';
    const autoLabel = t.autoApply ? 'AUTO' : 'MANUAL';
    const autoStyle: React.CSSProperties = {
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 6,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        background: t.autoApply ? '#D1FAE5' : TPL_BG,
        color: t.autoApply ? '#065F46' : TPL_SLATE,
    };
    const scheduleSubtext = t.autoApply
        ? `Auto-apply on next due date \u00b7 ${nextApplyLabel}`
        : 'Manual apply';

    // Static history items (mock data)
    const historyItems = [
        { label: `Applied to May 2026`, date: lastAppliedLong, count: entryCount },
        { label: 'Applied to Apr 2026', date: 'Apr 1, 2026', count: entryCount },
        { label: 'Edited \u2014 added \u201cNYT\u201d', date: 'Mar 22, 2026', count: null },
    ];

    return (
        <>
            {/* Header */}
            <div style={{ padding: '20px 22px 16px', borderBottom: `1px solid ${TPL_HAIR}`, flexShrink: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: colorSwatch.bg }}>
                        <TplIcon name={t.icon} size={24} color={colorSwatch.fg} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>{t.name}</div>
                        <div style={{ fontSize: 12, color: TPL_SLATE, marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: TPL_PURPLE_SOFT, color: TPL_PURPLE_DARK, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <TplIcon name="event_repeat" size={11} color={TPL_PURPLE_DARK} />
                                {recLabel}
                            </span>
                            <span>&middot;</span>
                            <span>{t.account}</span>
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <div
                        onClick={onApply}
                        style={{ flex: 1, height: 40, borderRadius: 8, background: TPL_PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                        <TplIcon name="check" size={14} color="#fff" />
                        Apply to month&hellip;
                    </div>
                    <div
                        onClick={onEdit}
                        style={{ width: 40, height: 40, borderRadius: 8, background: '#fff', border: `1px solid ${TPL_HAIR}`, color: TPL_INK_700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        title="Edit"
                    >
                        <TplIcon name="edit" size={16} color={TPL_INK_700} />
                    </div>
                    <div
                        onClick={onDuplicate}
                        style={{ width: 40, height: 40, borderRadius: 8, background: '#fff', border: `1px solid ${TPL_HAIR}`, color: TPL_INK_700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        title="Duplicate"
                    >
                        <TplIcon name="content_copy" size={16} color={TPL_INK_700} />
                    </div>
                    <div
                        onClick={onTogglePause}
                        style={{ width: 40, height: 40, borderRadius: 8, background: '#fff', border: `1px solid ${TPL_HAIR}`, color: TPL_INK_700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        title={pauseBtnTitle}
                    >
                        <TplIcon name={pauseBtnIcon} size={16} color={TPL_INK_700} />
                    </div>
                    <div
                        onClick={onMore}
                        style={{ width: 40, height: 40, borderRadius: 8, background: '#fff', border: `1px solid ${TPL_HAIR}`, color: TPL_INK_700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                        title="More"
                    >
                        <TplIcon name="more_vert" size={16} color={TPL_INK_700} />
                    </div>
                </div>
            </div>

            {/* Scrollable detail body */}
            <div style={{ flex: 1, overflow: 'auto', padding: '16px 22px 24px', minHeight: 0 }}>
                {/* Summary grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                    <div style={{ background: TPL_BG, borderRadius: 10, padding: '10px 12px', border: `1px solid ${TPL_HAIR}` }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Expenses</div>
                        <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: TPL_RED }}>{expenseLabel}</div>
                        <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2 }}>per cycle</div>
                    </div>
                    {income > 0 ? (
                        <div style={{ background: TPL_BG, borderRadius: 10, padding: '10px 12px', border: `1px solid ${TPL_HAIR}` }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Income</div>
                            <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums', color: TPL_GREEN }}>{incomeLabel}</div>
                            <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2 }}>per cycle</div>
                        </div>
                    ) : (
                        <div style={{ background: TPL_BG, borderRadius: 10, padding: '10px 12px', border: `1px solid ${TPL_HAIR}` }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Entries</div>
                            <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{entryCount}</div>
                            <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2 }}>{tagsSub}</div>
                        </div>
                    )}
                    <div style={{ background: TPL_BG, borderRadius: 10, padding: '10px 12px', border: `1px solid ${TPL_HAIR}` }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Applied</div>
                        <div style={{ fontSize: 15, fontWeight: 800, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{t.appliedCount}{'\u00d7'}</div>
                        <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2 }}>last {lastAppliedLabel}</div>
                    </div>
                </div>

                {/* Entries list */}
                <div style={{ marginTop: 22 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: TPL_INK }}>Entries ({entryCount})</span>
                        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: TPL_PURPLE, cursor: 'pointer' }} onClick={onAddEntry}>+ Add entry</span>
                    </div>
                    {t.entries.map((e, i) => {
                        const cat = TPL_CAT[e.cat] || TPL_CAT['other'];
                        const isPos = e.amount > 0;
                        const amtLabel = tplFmt(e.amount);
                        const dayLabel = e.day ? `day ${e.day}` : '\u2014';
                        return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, border: `1px solid ${TPL_HAIR}`, background: '#fff', marginBottom: 6 }}>
                                <span style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: cat.bg }}>
                                    <TplIcon name={cat.icon} size={14} color={cat.fg} />
                                </span>
                                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, minWidth: 0 }}>{e.title}</span>
                                <span style={{ fontSize: 11, color: TPL_SLATE, fontVariantNumeric: 'tabular-nums' }}>{dayLabel}</span>
                                <span style={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 80, textAlign: 'right', color: isPos ? TPL_GREEN : TPL_RED }}>
                                    {amtLabel}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Category breakdown */}
                <div style={{ marginTop: 22 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: TPL_INK }}>By category</span>
                    </div>
                    <CategoryBreakdown t={t} />
                </div>

                {/* Schedule */}
                <div style={{ marginTop: 22 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: TPL_INK }}>Schedule</span>
                        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: TPL_PURPLE, cursor: 'pointer' }} onClick={onEdit}>Edit</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, border: `1px solid ${TPL_HAIR}`, background: '#fff' }}>
                        <span style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: TPL_PURPLE_SOFT }}>
                            <TplIcon name="event_repeat" size={14} color={TPL_PURPLE} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>{recLabel}</div>
                            <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2 }}>{scheduleSubtext}</div>
                        </div>
                        <span style={autoStyle}>{autoLabel}</span>
                    </div>
                </div>

                {/* Notes */}
                {t.notes ? (
                    <div style={{ marginTop: 22 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: TPL_INK }}>Notes</span>
                        </div>
                        <div style={{ padding: '10px 12px', background: TPL_BG, border: `1px solid ${TPL_HAIR}`, borderRadius: 8, fontSize: 12, color: TPL_INK_700, lineHeight: 1.5 }}>
                            {t.notes}
                        </div>
                    </div>
                ) : null}

                {/* History */}
                <div style={{ marginTop: 22, paddingBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: TPL_INK }}>History</span>
                        <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: TPL_PURPLE, cursor: 'pointer' }}>View all</span>
                    </div>
                    {historyItems.map((h, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, border: `1px solid ${TPL_HAIR}`, background: '#fff', marginBottom: 6 }}>
                            <span style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: TPL_BG }}>
                                <TplIcon name="history" size={14} color={TPL_SLATE} />
                            </span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600 }}>{h.label}</div>
                                <div style={{ fontSize: 11, color: TPL_SLATE, marginTop: 2 }}>{h.date}</div>
                            </div>
                            {h.count != null && (
                                <span style={{ fontSize: 11, color: TPL_SLATE }}>+{h.count} entries</span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

// ── Main page ──

// Map API EntryTemplate to rich Template for the UI
const apiToTemplate = (apiTpl: any): Template => ({
    id: String(apiTpl.id || ''),
    name: String(apiTpl.name || ''),
    color: String(apiTpl.color || 'purple'),
    icon: String(apiTpl.icon || 'bookmark'),
    recurrence: apiTpl.recurrence || { kind: 'monthly', anchor: 1 },
    nextApply: apiTpl.nextApply || null,
    lastApplied: apiTpl.lastApplied || apiTpl.updatedAt || null,
    autoApply: !!apiTpl.autoApply,
    account: String(apiTpl.account || 'Personal'),
    tags: Array.isArray(apiTpl.tags) ? apiTpl.tags : [],
    notes: String(apiTpl.notes || ''),
    appliedCount: Number(apiTpl.appliedCount || 0),
    status: apiTpl.status || 'active',
    entries: Array.isArray(apiTpl.entries) ? apiTpl.entries.map((e: any) => ({
        title: String(e.title || ''),
        amount: Number(e.amount || 0),
        day: Number(e.day || 0),
        cat: String(e.cat || e.category || 'other'),
    })) : [],
});

export const TemplatesPage: React.FC = () => {
    const login: any = useSelector(mediateLoginInfoDependent);
    const selectedList: any = useSelector(mediateSelectedListInfoDependent);
    const userId = login?.user?.id || '';
    const listId = selectedList?.id || '';

    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<'all' | 'auto' | 'paused'>('all');
    const [searchText, setSearchText] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Modal state
    const [createEditOpen, setCreateEditOpen] = useState(false);
    const [createEditMode, setCreateEditMode] = useState<'create' | 'edit'>('create');
    const [applyOpen, setApplyOpen] = useState(false);

    // Fetch templates from API with mock fallback
    const loadTemplates = useCallback(async () => {
        if (!userId) return;
        setLoading(true);
        try {
            const res = await fetchTemplatesApi(userId);
            const apiTemplates = (res.templates || []).map(apiToTemplate);
            if (apiTemplates.length > 0) {
                setTemplates(apiTemplates);
                setSelectedId(prev => prev || apiTemplates[0].id);
            } else {
                // No templates yet — show mock data for demo
                const mocks = MOCK_TEMPLATES.map(t => ({ ...t, entries: t.entries.map(e => ({ ...e })) }));
                setTemplates(mocks);
                setSelectedId(prev => prev || mocks[0].id);
            }
        } catch {
            // API not ready — use mock data
            const mocks = MOCK_TEMPLATES.map(t => ({ ...t, entries: t.entries.map(e => ({ ...e })) }));
            setTemplates(mocks);
            setSelectedId(prev => prev || mocks[0].id);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { loadTemplates(); }, [loadTemplates]);

    // Derived counts from full (non-filtered) list
    const allCount = templates.filter(x => x.status !== 'archived').length;
    const autoCount = templates.filter(x => x.autoApply && x.status === 'active').length;
    const pausedCount = templates.filter(x => x.status === 'paused').length;

    const filteredList = templates.filter(t => {
        const notArchived = t.status !== 'archived';
        const matchesFilter =
            filter === 'auto' ? t.autoApply && t.status === 'active' :
            filter === 'paused' ? t.status === 'paused' :
            notArchived;
        const lowerSearch = searchText.toLowerCase();
        const matchesSearch = !lowerSearch || t.name.toLowerCase().includes(lowerSearch);
        return matchesFilter && matchesSearch;
    });

    const activeList = filteredList.filter(t => t.status === 'active');
    const pausedList = filteredList.filter(t => t.status === 'paused');

    const selected = templates.find(t => t.id === selectedId) || null;

    const filterChips: Array<{ id: 'all' | 'auto' | 'paused'; label: string }> = [
        { id: 'all',    label: `All \u00b7 ${allCount}` },
        { id: 'auto',   label: `Auto \u00b7 ${autoCount}` },
        { id: 'paused', label: `Paused \u00b7 ${pausedCount}` },
    ];

    // ── Action handlers ──

    const handleOpenCreate = () => {
        setCreateEditMode('create');
        setCreateEditOpen(true);
    };

    const handleOpenEdit = () => {
        if (!selected) return;
        setCreateEditMode('edit');
        setCreateEditOpen(true);
    };

    const handleCloseCreateEdit = () => setCreateEditOpen(false);

    const handleSaveTemplate = async (updated: Template) => {
        // Optimistic update
        setTemplates(ts => {
            const exists = ts.find(t => t.id === updated.id);
            if (exists) {
                return ts.map(t => t.id === updated.id ? updated : t);
            }
            return [...ts, updated];
        });
        setSelectedId(updated.id);
        setCreateEditOpen(false);

        // Persist to API
        try {
            const isNew = !templates.find(t => t.id === updated.id);
            const apiEntries = updated.entries.map(e => ({
                title: e.title,
                amount: e.amount,
                type: e.amount < 0 ? 'DEBIT' : 'CREDIT',
                category: e.cat,
            }));
            if (isNew) {
                const res = await createTemplateApi(userId, updated.name, apiEntries);
                // Update local ID with server-assigned ID
                const serverId = res.template?.id;
                if (serverId && serverId !== updated.id) {
                    setTemplates(ts => ts.map(t => t.id === updated.id ? { ...t, id: serverId } : t));
                    setSelectedId(serverId);
                }
            } else {
                await updateTemplateApi(updated.id, { name: updated.name, entries: apiEntries });
            }
        } catch {
            // API failed — local state already updated, log silently
        }
    };

    const handleDeleteTemplate = async (id: string) => {
        setTemplates(ts => ts.filter(t => t.id !== id));
        setSelectedId(prev => {
            if (prev === id) {
                const remaining = templates.filter(t => t.id !== id);
                return remaining[0]?.id || null;
            }
            return prev;
        });
        setCreateEditOpen(false);

        try {
            await deleteTemplateApi(id);
        } catch {
            // API failed — already removed locally
        }
    };

    const handleDuplicate = () => {
        if (!selected) return;
        const newId = `${selected.id}-copy-${Date.now()}`;
        const copy: Template = {
            ...selected,
            id: newId,
            name: `${selected.name} (copy)`,
            appliedCount: 0,
            lastApplied: null,
            nextApply: selected.nextApply,
        };
        setTemplates(ts => [...ts, copy]);
        setSelectedId(newId);
    };

    const handleTogglePause = () => {
        if (!selected) return;
        const newStatus = selected.status === 'paused' ? 'active' : 'paused';
        setTemplates(ts => ts.map(t => t.id === selected.id ? { ...t, status: newStatus } : t));
    };

    const handleMore = () => {
        if (!selected) return;
        if (window.confirm(`Delete "${selected.name}"? This cannot be undone.`)) {
            handleDeleteTemplate(selected.id);
        }
    };

    const handleOpenApply = () => {
        if (!selected) return;
        setApplyOpen(true);
    };

    const handleCloseApply = () => setApplyOpen(false);

    return (
        <div style={{ width: '100%', height: '100%', background: TPL_BG, display: 'flex', fontFamily: 'var(--font-sans)', color: TPL_INK, minHeight: 0, overflow: 'hidden' }}>
            {/* Left pane */}
            <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${TPL_HAIR}`, minWidth: 0 }}>
                {/* List header */}
                <div style={{ padding: '18px 24px 14px', background: '#fff', borderBottom: `1px solid ${TPL_HAIR}`, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div>
                            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>Templates</div>
                            <div style={{ fontSize: 13, color: TPL_SLATE, marginTop: 2 }}>Reusable sets of entries you can apply to any month.</div>
                        </div>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button
                                style={{ height: 38, padding: '0 14px', borderRadius: 8, background: '#fff', color: TPL_INK_700, border: `1px solid ${TPL_HAIR}`, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                            >
                                <TplIcon name="schedule" size={14} color={TPL_INK_700} />
                                Schedule
                            </button>
                            <button
                                onClick={handleOpenCreate}
                                style={{ height: 38, padding: '0 14px', borderRadius: 8, background: TPL_PURPLE, color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer', border: 'none' }}
                            >
                                <TplIcon name="add" size={16} color="#fff" />
                                New template
                            </button>
                        </div>
                    </div>

                    {/* Toolbar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
                        <div style={{ flex: 1, height: 36, borderRadius: 8, background: TPL_BG, border: `1px solid ${TPL_HAIR}`, padding: '0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <TplIcon name="search" size={16} color={TPL_SLATE} />
                            <input
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                placeholder="Search templates\u2026"
                                style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: TPL_SLATE, width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            {filterChips.map(c => {
                                const isActive = filter === c.id;
                                return (
                                    <div
                                        key={c.id}
                                        onClick={() => setFilter(c.id)}
                                        style={{
                                            height: 30,
                                            padding: '0 12px',
                                            borderRadius: 15,
                                            background: isActive ? TPL_PURPLE_SOFT : '#fff',
                                            border: isActive ? 'none' : `1px solid ${TPL_HAIR}`,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: isActive ? TPL_PURPLE_DARK : TPL_INK_700,
                                            cursor: 'pointer',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                        }}
                                    >
                                        {c.label}
                                    </div>
                                );
                            })}
                            <div style={{ height: 30, padding: '0 12px', borderRadius: 15, background: '#fff', border: `1px solid ${TPL_HAIR}`, fontSize: 12, fontWeight: 600, color: TPL_INK_700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <TplIcon name="sort" size={12} color={TPL_INK_700} />
                                Recent
                            </div>
                        </div>
                    </div>
                </div>

                {/* List scroll area */}
                <div style={{ flex: 1, overflow: 'auto', padding: '14px 16px 24px' }}>
                    {activeList.length > 0 && (
                        <>
                            <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '14px 8px 8px' }}>
                                Active &middot; {activeList.length}
                            </div>
                            {activeList.map(tpl => (
                                <TemplateRow
                                    key={tpl.id}
                                    t={tpl}
                                    selected={selected?.id === tpl.id}
                                    onSelect={() => setSelectedId(tpl.id)}
                                />
                            ))}
                        </>
                    )}
                    {pausedList.length > 0 && (
                        <>
                            <div style={{ fontSize: 10, fontWeight: 700, color: TPL_SLATE_LIGHT, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '14px 8px 8px' }}>
                                Paused &middot; {pausedList.length}
                            </div>
                            {pausedList.map(tpl => (
                                <TemplateRow
                                    key={tpl.id}
                                    t={tpl}
                                    selected={selected?.id === tpl.id}
                                    onSelect={() => setSelectedId(tpl.id)}
                                />
                            ))}
                        </>
                    )}
                    {loading && filteredList.length === 0 && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                            <span style={{ fontSize: 13, color: TPL_SLATE }}>Loading…</span>
                        </div>
                    )}
                    {!loading && filteredList.length === 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', color: TPL_SLATE, textAlign: 'center' }}>
                            <TplIcon name="search" size={32} color={TPL_SLATE_LIGHT} />
                            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 12, color: TPL_INK }}>No templates found</div>
                            <div style={{ fontSize: 13, marginTop: 6 }}>Try a different search or filter.</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Right pane */}
            <div style={{ width: 440, flexShrink: 0, display: 'flex', flexDirection: 'column', background: '#fff', minHeight: 0 }}>
                <DetailPane
                    t={selected}
                    onApply={handleOpenApply}
                    onEdit={handleOpenEdit}
                    onDuplicate={handleDuplicate}
                    onTogglePause={handleTogglePause}
                    onMore={handleMore}
                    onAddEntry={handleOpenEdit}
                />
            </div>

            {/* Modals */}
            {createEditOpen && (
                <CreateEditModal
                    template={createEditMode === 'edit' ? selected : null}
                    mode={createEditMode}
                    onClose={handleCloseCreateEdit}
                    onSave={handleSaveTemplate}
                    onDelete={handleDeleteTemplate}
                />
            )}
            {applyOpen && selected && (
                <ApplyModal
                    template={selected}
                    userId={userId}
                    listId={listId}
                    onClose={handleCloseApply}
                    onApplied={loadTemplates}
                />
            )}
        </div>
    );
};
